import React from 'react'
import * as R from 'remeda'

import {
  type CustomerAttribute,
  type CustomerAttributeGroup,
  useSurveyQueryCustomerAttributeGroups,
} from '~/hooks/useSurveyQuery'
import { compact, exists, findByPath, type GroupAndAll } from '~/utils'

import { useAtomValueContext } from '../providers/AtomsProvider'
import { type GroupsDiffColumnKey, type RowData } from '../utils'

import { useMapGroup } from './useMapGroup'
import { useMapGroupsDiff } from './useMapGroupsDiff'
import { useTotalData } from './useTotalData'

type CustomerAttributeData = RowData<GroupAndAll | GroupsDiffColumnKey> & {
  customerAttributeGroup: CustomerAttributeGroup
  customerAttribute: CustomerAttribute
}

export const useCustomerAttributesArrayData = (): CustomerAttributeData[][] => {
  const customerAttributeQueryData = useAtomValueContext(
    'customerAttributeQueryData',
  )
  const customerAttributeGroups = useSurveyQueryCustomerAttributeGroups()
  const mapGroup = useMapGroup()
  const mapGroupsDiff = useMapGroupsDiff()
  const totalData = useTotalData()

  return React.useMemo(
    () =>
      R.pipe(
        customerAttributeGroups,
        R.map((customerAttributeGroup) =>
          R.pipe(
            customerAttributeGroup.customerAttributes,
            R.map((customerAttribute) => {
              const result = R.pipe(
                customerAttributeQueryData ?? [],
                findByPath('customerAttributeId', customerAttribute.id),
                (customerAttributeCount) => customerAttributeCount?.result,
              )
              if (!exists(result)) {
                return null
              }

              const getGroupCount = (group: GroupAndAll) =>
                (group === 'all'
                  ? result.totalCount
                  : findByPath(result.groupResults, 'group', group)?.count) ??
                NaN

              return {
                customerAttributeGroup,
                customerAttribute,
                counts: {
                  ...mapGroup(getGroupCount),
                  ...mapGroupsDiff(
                    ([left, right]) =>
                      getGroupCount(left) - getGroupCount(right),
                  ),
                },
              }
            }),
            compact,
            R.map((customerAttributeData) => {
              const groupRatios = mapGroup(
                (group) =>
                  customerAttributeData.counts[group] / totalData.counts[group],
              )

              const groupsToCompareRatios = mapGroupsDiff(
                ([left, right]) => groupRatios[left] - groupRatios[right],
              )

              return {
                ...customerAttributeData,
                ratios: { ...groupRatios, ...groupsToCompareRatios },
              }
            }),
          ),
        ),
        R.filter((customerAttributes) => 0 < customerAttributes.length),
      ),
    [
      customerAttributeGroups,
      customerAttributeQueryData,
      mapGroup,
      mapGroupsDiff,
      totalData.counts,
    ],
  )
}
